import { escapeHtml } from "./escapeHtml";
import { consumeOAuthReturnPath, persistOAuthTokenResponse, readOAuthPkce, clearOAuthPkce } from "./oauthSession";
import { isOAuthConfigured, oauthTokenUrl, redirectToTokoOAuthLogin } from "./oauthStart";

function renderStatus(root: HTMLElement, title: string, text: string, retry = false): void {
  root.innerHTML = `
    <div class="shell">
      <header class="hero">
        <p class="eyebrow">Toko</p>
        <h1 class="hero-title">${escapeHtml(title)}</h1>
        <p class="hero-lead">${escapeHtml(text)}</p>
      </header>
      ${
        retry
          ? `<section class="panel"><div class="panel-inner">
              <div class="actions">
                <button type="button" class="btn primary" id="btn-retry">Войти с аккаунтом Toko</button>
              </div>
            </div></section>`
          : `<section class="panel"><div class="panel-inner wait">
              <div class="spinner" aria-hidden="true"></div>
              <div>
                <h2 class="panel-title">Подождите</h2>
                <p class="panel-text">Завершаем вход…</p>
              </div>
            </div></section>`
      }
    </div>
  `;
  root.querySelector("#btn-retry")?.addEventListener("click", () => {
    void redirectToTokoOAuthLogin({ returnPath: "/" }).catch((e) => {
      renderStatus(root, "Не удалось войти", e instanceof Error ? e.message : String(e), true);
    });
  });
}

function isTauriShell(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

async function postOAuthToken(
  tokenUrl: string,
  body: URLSearchParams,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  if (isTauriShell()) {
    const { invoke } = await import("@tauri-apps/api/core");
    try {
      const raw = await invoke<string>("oauth_exchange_token", {
        tokenUrl,
        body: body.toString(),
      });
      const data = JSON.parse(raw) as Record<string, unknown>;
      return { ok: true, status: 200, data };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      const http = message.match(/token_exchange_http_(\d+):(.*)/s);
      if (http) {
        const status = Number(http[1]);
        let data: Record<string, unknown> = {};
        try {
          data = JSON.parse(http[2] ?? "{}") as Record<string, unknown>;
        } catch {
          data = { error_description: http[2] || message };
        }
        return { ok: false, status, data };
      }
      throw new Error(message.replace(/^token_exchange_failed:\s*/i, "") || message);
    }
  }

  const res = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, data };
}

async function exchangeCode(code: string, verifier: string): Promise<void> {
  const redirect = import.meta.env.VITE_TOKO_OAUTH_REDIRECT_URI?.trim();
  const clientId = import.meta.env.VITE_TOKO_OAUTH_CLIENT_ID?.trim();
  if (!redirect || !clientId) {
    throw new Error("Вход временно недоступен. Обратитесь к администратору.");
  }
  const body = new URLSearchParams();
  body.set("grant_type", "authorization_code");
  body.set("client_id", clientId);
  body.set("code", code);
  body.set("redirect_uri", redirect);
  body.set("code_verifier", verifier);

  const { ok, status, data } = await postOAuthToken(oauthTokenUrl(), body);
  if (!ok) {
    const msg =
      (data.error_description as string) ||
      (data.message as string) ||
      (data.error as string) ||
      `Ошибка обмена кода (${status})`;
    throw new Error(msg);
  }
  const access = typeof data.access_token === "string" ? data.access_token : "";
  if (!access) {
    throw new Error("Сервер не вернул токен доступа");
  }
  persistOAuthTokenResponse({
    access_token: access,
    expires_in: Number(data.expires_in),
    refresh_token: typeof data.refresh_token === "string" ? data.refresh_token : null,
  });
}

export async function mountOAuthCallback(root: HTMLElement): Promise<void> {
  if (!isOAuthConfigured()) {
    renderStatus(root, "Вход не настроен", "Укажите OAuth-переменные сборки мини-приложения.", false);
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const oauthErr = params.get("error");
  const stateFromAuth = params.get("state");
  const pk = readOAuthPkce();

  if (oauthErr) {
    const desc = params.get("error_description");
    const stateOk = Boolean(stateFromAuth && pk?.state && stateFromAuth === pk.state);
    if (stateOk) clearOAuthPkce();
    renderStatus(
      root,
      "Не удалось войти",
      stateOk
        ? desc || oauthErr
        : "Сессия входа устарела. Начните вход заново.",
      true,
    );
    return;
  }

  if (!stateFromAuth || !pk || pk.state !== stateFromAuth) {
    renderStatus(root, "Сессия входа устарела", "Начните вход заново с аккаунтом Toko.", true);
    return;
  }

  const code = params.get("code");
  if (!code) {
    renderStatus(root, "Нет кода авторизации", "Попробуйте войти снова.", true);
    return;
  }

  renderStatus(root, "Входим…", "Подождите несколько секунд.", false);
  try {
    await exchangeCode(code, pk.verifier);
    clearOAuthPkce();
    const next = consumeOAuthReturnPath();
    window.location.replace(next);
  } catch (e) {
    renderStatus(root, "Не удалось войти", e instanceof Error ? e.message : String(e), true);
  }
}
